import { Alert, AlertIcon, Box, Button, Divider, Flex, Input, Radio, RadioGroup, Text } from '@chakra-ui/react'
import { useEffect, useMemo, useState } from 'react'

import { FormModal } from '../../../../components/FormModal'
import { LoadingBlock } from '../../../../components/Loading'
import { useAppToast } from '../../../../helpers'
import { EventSeriesService } from '../../../../services/Event/EventSeriesService'
import { EventSeriesPickerItem } from '../../../../services/Event/types'

interface IProps {
  isOpen: boolean
  onClose: () => void
  onConfirm: (seriesId: number) => Promise<void> | void
  /** Hidden from the list (e.g. the series the event currently belongs to). */
  excludeSeriesId?: number
  title: string
  confirmLabel: string
  /** Optional informational note shown above the list. */
  note?: string
  /** When provided, shows a "create new series" shortcut above the list. */
  onCreateSeries?: () => void
}

/** Modal that lists every series and lets the user pick one as a target. */
export function SeriesPickerModal({
  isOpen,
  onClose,
  onConfirm,
  excludeSeriesId,
  title,
  confirmLabel,
  note,
  onCreateSeries
}: IProps) {
  const toast = useAppToast()
  const [series, setSeries] = useState<EventSeriesPickerItem[]>([])
  const [loading, setLoading] = useState(false)
  const [search, setSearch] = useState('')
  const [selected, setSelected] = useState<string>('')
  const [submitting, setSubmitting] = useState(false)

  useEffect(() => {
    if (!isOpen) return
    setSearch('')
    setSelected('')
    setLoading(true)
    new EventSeriesService()
      .listForPicker()
      .then((data) => setSeries(data.filter((s) => s.id !== excludeSeriesId)))
      .catch(() => toast.error('Não foi possível carregar as séries', 'Tente novamente.'))
      .finally(() => setLoading(false))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, excludeSeriesId])

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase()
    if (!term) return series
    return series.filter((s) => s.name.toLowerCase().includes(term))
  }, [series, search])

  const handleConfirm = async () => {
    if (!selected) return
    setSubmitting(true)
    try {
      await onConfirm(Number(selected))
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <FormModal
      isOpen={isOpen}
      onClose={onClose}
      title={title}
      onSubmit={handleConfirm}
      confirmLabel={confirmLabel}
      isLoading={submitting}
      isConfirmDisabled={!selected}
    >
      {note && (
        <Alert status="info" fontSize="sm" borderRadius="md" mb={4}>
          <AlertIcon />
          {note}
        </Alert>
      )}

      {onCreateSeries && (
        <Box mb={4}>
          <Button colorScheme="magenta" variant="outline" w="100%" onClick={onCreateSeries}>
            + Criar nova série de eventos
          </Button>
          <Flex align="center" gap={3} mt={3}>
            <Divider />
            <Text variant="caption" whiteSpace="nowrap">
              ou escolha uma série existente
            </Text>
            <Divider />
          </Flex>
        </Box>
      )}

      <Input
        placeholder="Buscar série pelo nome..."
        size="sm"
        mb={3}
        value={search}
        onChange={(e) => setSearch(e.target.value)}
      />

      {loading ? (
        <LoadingBlock />
      ) : filtered.length === 0 ? (
        <Text variant="caption" textAlign="center" py={8}>
          {series.length === 0 ? 'Nenhuma série disponível.' : 'Nenhuma série encontrada.'}
        </Text>
      ) : (
        <RadioGroup value={selected} onChange={setSelected}>
          <Flex direction="column" gap={1}>
            {filtered.map((s) => (
              <Flex
                key={s.id}
                as="label"
                htmlFor={`series-${s.id}`}
                align="center"
                justify="space-between"
                p={2}
                borderWidth="1px"
                borderRadius="md"
                cursor="pointer"
                _hover={{ bg: 'gray.50' }}
                bg={selected === String(s.id) ? 'magenta.50' : undefined}
                borderColor={selected === String(s.id) ? 'magenta.300' : 'gray.200'}
              >
                <Radio id={`series-${s.id}`} value={String(s.id)}>
                  <Text fontSize="sm" fontWeight="500">
                    {s.name}
                  </Text>
                </Radio>
                <Text variant="caption" whiteSpace="nowrap">
                  {s.editions_count} {s.editions_count === 1 ? 'evento' : 'eventos'}
                </Text>
              </Flex>
            ))}
          </Flex>
        </RadioGroup>
      )}
    </FormModal>
  )
}
